import { useState, useMemo } from"react";
import { motion, AnimatePresence } from"framer-motion";
import { Check, X, ChevronRight, RotateCcw, Trophy, ListChecks } from"lucide-react";
import { QCM, pickRandom } from"@/data/qcm";

const N = 10; // nombre de questions par session

export default function Quiz({ onQuizResult }) {
 const [session, setSession] = useState(() => pickRandom(N));
 const [idx, setIdx] = useState(0);
 const [picked, setPicked] = useState(null); // index option choisie
 const [answers, setAnswers] = useState([]); // {qId, picked, correct}
 const [finished, setFinished] = useState(false);

 const q = session[idx];
 const correct = picked !== null && picked === q.correct;

 const validate = () => {
 if (picked === null) return;
 const isOk = picked === q.correct;
 setAnswers((a) => [...a, { qId: q.id, picked, correct: isOk }]);
 };

 const next = () => {
 if (idx + 1 >= session.length) {
 const finalScore = answers.filter((a) => a.correct).length + (picked === q.correct ? 1 : 0);
 setFinished(true);
 onQuizResult && onQuizResult(finalScore, session.length);
 } else {
 setIdx(idx + 1);
 setPicked(null);
 }
 };

 const restart = () => {
 setSession(pickRandom(N));
 setIdx(0);
 setPicked(null);
 setAnswers([]);
 setFinished(false);
 };

 const score = useMemo(() => answers.filter((a) => a.correct).length, [answers]);

 if (finished) {
 const total = session.length;
 const pct = Math.round((score / total) * 100);
 const grade =
 pct >= 90 ? { label:"Excellent !", color:"#2F7A5C", emoji:"🏆" } :
 pct >= 70 ? { label:"Très bien", color:"#2D6BE0", emoji:"🎯" } :
 pct >= 50 ? { label:"Pas mal", color:"#A56A2E", emoji:"📚" } :
 { label:"À retravailler", color:"#A33B4B", emoji:"💪" };

 return (
 <div data-testid="qcm-result" className="space-y-6">
 <motion.div
 initial={{ opacity: 0, scale: 0.95 }}
 animate={{ opacity: 1, scale: 1 }}
 className="bg-white border border-keyto rounded-2xl shadow-keyto p-8 text-center"
 style={{ borderTop: `4px solid ${grade.color}` }}
 >
 <div className="text-6xl mb-3">{grade.emoji}</div>
 <p className="text-sm font-bold uppercase tracking-wider mb-1" style={{ color: grade.color }}>{grade.label}</p>
 <h2 className="font-heading text-4xl font-extrabold text-[#15294F] mb-2">
 {score} / {total}
 </h2>
 <p className="text-slate-600">Score : <strong style={{ color: grade.color }}>{pct} %</strong></p>

 <div className="mt-6 grid grid-cols-3 gap-2 text-xs">
 {session.map((qq, i) => {
 const a = answers[i] || (i === idx ? { correct: picked === qq.correct } : null);
 const ok = a?.correct;
 return (
 <div
 key={qq.id}
 className={`p-2 rounded-md border text-left ${
 ok ?"bg-emerald-50 border-emerald-200" :"bg-rose-50 border-rose-200"
 }`}
 >
 <p className="font-mono text-[10px] text-slate-500">Q{i + 1}</p>
 <p className="text-[11px] text-slate-700 leading-tight mt-0.5">{qq.theme}</p>
 {ok ? (
 <Check size={12} className="text-emerald-600 mt-1" />
 ) : (
 <X size={12} className="text-rose-600 mt-1" />
 )}
 </div>
 );
 })}
 </div>

 <button
 data-testid="qcm-restart"
 onClick={restart}
 className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#2D6BE0] text-white font-bold hover:bg-[#15294F]"
 >
 <RotateCcw size={14} /> Recommencer un QCM
 </button>
 </motion.div>
 </div>
 );
 }

 const answered = picked !== null && answers.length === idx + 1;

 return (
 <div data-testid="qcm-view" className="space-y-6">
 {/* Header */}
 <div className="bg-gradient-to-br from-white to-slate-50 border border-keyto rounded-2xl shadow-keyto p-6">
 <div className="flex items-start gap-3">
 <div className="shrink-0 w-12 h-12 rounded-xl bg-[#2D6BE0]/10 border border-[#2D6BE0]/30 flex items-center justify-center">
 <ListChecks className="text-[#2D6BE0]" size={22} />
 </div>
 <div className="flex-1">
 <span className="inline-block px-3 py-1 text-xs font-semibold rounded-full border border-[#2D6BE0]/30 bg-blue-50 text-[#2D6BE0] mb-2">
 QCM · {N} questions
 </span>
 <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-[#15294F] leading-tight">
 Quiz interactif PCG
 </h1>
 <p className="mt-2 text-sm text-slate-600">
 Questions tirées au hasard parmi <strong>{QCM.length}</strong>. Une seule bonne réponse par question.
 Explication pédagogique après chaque validation.
 </p>
 </div>
 </div>

 {/* Progress */}
 <div className="mt-5 flex items-center gap-3">
 <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
 <div
 className="h-full bg-[#2D6BE0] transition-all"
 style={{ width: `${((idx + (picked !== null ? 1 : 0)) / session.length) * 100}%` }}
 />
 </div>
 <span className="text-xs font-mono font-bold text-[#15294F]">
 Q{idx + 1} / {session.length}
 </span>
 <span className="text-xs text-slate-500">
 <Trophy size={12} className="inline mr-0.5" />
 {score} ok
 </span>
 </div>
 </div>

 {/* Question */}
 <AnimatePresence mode="wait">
 <motion.div
 key={q.id}
 initial={{ opacity: 0, y: 10 }}
 animate={{ opacity: 1, y: 0 }}
 exit={{ opacity: 0, y: -10 }}
 transition={{ duration: 0.25 }}
 className="bg-white border border-keyto rounded-2xl shadow-keyto-sm p-6 md:p-8"
 >
 <p className="text-[10px] font-bold uppercase text-[#2D6BE0] tracking-wider mb-2">
 {q.theme}
 </p>
 <h2 className="font-heading text-xl font-bold text-[#15294F] leading-snug mb-5" data-testid={`qcm-question-${q.id}`}>
 {q.q}
 </h2>

 <div className="space-y-2">
 {q.options.map((opt, i) => {
 const isPicked = picked === i;
 const isCorrect = i === q.correct;
 const showResult = answered;
 let cls ="border-keyto bg-white hover:border-[#2D6BE0]/40 hover:bg-slate-50";
 if (showResult && isCorrect) cls ="border-emerald-400 bg-emerald-50";
 else if (showResult && isPicked && !isCorrect) cls ="border-rose-400 bg-rose-50";
 else if (isPicked && !showResult) cls ="border-[#2D6BE0] bg-blue-50";
 return (
 <button
 key={i}
 data-testid={`qcm-option-${i}`}
 onClick={() => !answered && setPicked(i)}
 disabled={answered}
 className={`w-full text-left px-4 py-3 rounded-xl border-2 transition flex items-center gap-3 ${cls} ${answered ?"cursor-default" :""}`}
 >
 <span className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
 showResult && isCorrect ?"bg-emerald-500 text-white" :
 showResult && isPicked && !isCorrect ?"bg-rose-500 text-white" :
 isPicked ?"bg-[#2D6BE0] text-white" :"bg-slate-100 text-slate-500"
 }`}>
 {showResult && isCorrect ? <Check size={12} /> :
 showResult && isPicked && !isCorrect ? <X size={12} /> :
 String.fromCharCode(65 + i)}
 </span>
 <span className="text-sm font-medium text-[#15294F] flex-1">{opt}</span>
 </button>
 );
 })}
 </div>

 {/* Explication après validation */}
 {answered && (
 <motion.div
 initial={{ opacity: 0, y: 8 }}
 animate={{ opacity: 1, y: 0 }}
 className={`mt-5 p-4 rounded-xl border ${
 correct ?"bg-emerald-50 border-emerald-200" :"bg-rose-50 border-rose-200"
 }`}
 data-testid="qcm-explanation"
 >
 <p className={`text-xs font-bold uppercase tracking-wider mb-1 ${correct ?"text-emerald-700" :"text-rose-700"}`}>
 {correct ?"Bonne réponse !" :"Mauvaise réponse"}
 </p>
 <p className="text-sm text-slate-700 leading-relaxed">{q.explication}</p>
 </motion.div>
 )}

 {/* Boutons */}
 <div className="mt-6 flex items-center justify-end gap-2">
 {!answered ? (
 <button
 data-testid="qcm-validate"
 onClick={validate}
 disabled={picked === null}
 className="px-5 py-2 rounded-lg bg-[#2D6BE0] text-white font-bold text-sm hover:bg-[#15294F] disabled:opacity-40 disabled:cursor-not-allowed"
 >
 Valider
 </button>
 ) : (
 <button
 data-testid="qcm-next"
 onClick={next}
 className="inline-flex items-center gap-1 px-5 py-2 rounded-lg bg-[#15294F] text-white font-bold text-sm hover:bg-[#2D6BE0]"
 >
 {idx + 1 < session.length ?"Question suivante" :"Voir mon score"} <ChevronRight size={14} />
 </button>
 )}
 </div>
 </motion.div>
 </AnimatePresence>
 </div>
 );
}
